import { Card, Stack, Badge, Select, Group, Drawer, Code, Title, Text, Divider, Tabs, TextInput } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useDebouncedValue } from "@mantine/hooks";
import { useState } from "react";
import { useInfiniteResource } from "../../../shared/api";
import { PageHeader, EmptyState, InfiniteTable } from "../../../shared/ui";

const SEVERITY_COLOR: Record<string, string> = {
  LOW: "gray", NORMAL: "blue", HIGH: "red",
};

const ACTION_COLOR: Record<string, string> = {
  CREATE: "green", UPDATE: "blue", DELETE: "red",
};

export function AuditLogPage() {
  const [severity, setSeverity] = useState<string | null>(null);
  const [action, setAction] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [openLog, setOpenLog] = useState<any | null>(null);
  const {
    items: list,
    total,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("audit-logs", {
    search: debouncedSearch,
    filters: {
      ...(severity ? { severity } : {}),
      ...(action ? { action } : {}),
    },
  });

  return (
    <Stack gap="md">
      <PageHeader title="Denetim Kaydı (Audit Log)" subtitle={`${total} kayıt`} />
      <Group>
        <TextInput
          placeholder="Model veya obje ID ile ara..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
        <Select placeholder="Önem" clearable value={severity} onChange={setSeverity}
          data={[{ value: "HIGH", label: "Yüksek" }, { value: "NORMAL", label: "Normal" }, { value: "LOW", label: "Düşük" }]} />
        <Select placeholder="İşlem" clearable value={action} onChange={setAction}
          data={[{ value: "CREATE", label: "Oluştur" }, { value: "UPDATE", label: "Güncelle" }, { value: "DELETE", label: "Sil" }]} />
      </Group>

      <Card padding="sm" radius="md" withBorder>
        <InfiniteTable
          rows={list}
          rowKey={(a) => a.id}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onRowClick={(a) => setOpenLog(a)}
          emptyContent={<EmptyState title="Audit log yok" />}
          columns={[
            {
              header: "Zaman",
              render: (a) => (
                <span style={{ fontSize: 11 }}>{new Date(a.timestamp).toLocaleString("tr-TR")}</span>
              ),
            },
            { header: "Kullanıcı", render: (a) => a.actor_username || "system" },
            { header: "Model", render: (a) => <Badge variant="light">{a.model_name}</Badge> },
            {
              header: "Obje",
              render: (a) => (
                <span style={{ fontSize: 10, fontFamily: "monospace" }}>
                  {a.object_id?.substring(0, 12)}
                </span>
              ),
            },
            {
              header: "İşlem",
              render: (a) => <Badge color={ACTION_COLOR[a.action]} size="sm">{a.action}</Badge>,
            },
            {
              header: "Önem",
              render: (a) => <Badge color={SEVERITY_COLOR[a.severity]} size="sm">{a.severity}</Badge>,
            },
            { header: "IP", render: (a) => a.actor_ip || "-" },
          ]}
        />
      </Card>

      <Drawer
        opened={!!openLog}
        onClose={() => setOpenLog(null)}
        position="right"
        size="lg"
        title="Audit Log Detayı"
      >
        {openLog && (
          <Stack gap="md">
            <Card padding="md" radius="md" withBorder>
              <Group justify="space-between">
                <Badge color={ACTION_COLOR[openLog.action]}>{openLog.action}</Badge>
                <Badge color={SEVERITY_COLOR[openLog.severity]}>{openLog.severity}</Badge>
              </Group>
              <Divider my="sm" />
              <Stack gap={4}>
                <Text size="sm"><b>Model:</b> {openLog.model_name}</Text>
                <Text size="sm"><b>Obje ID:</b> <code>{openLog.object_id}</code></Text>
                <Text size="sm"><b>Kullanıcı:</b> {openLog.actor_username || "system"}</Text>
                <Text size="sm"><b>IP:</b> {openLog.actor_ip || "-"}</Text>
                <Text size="sm"><b>User-Agent:</b> {openLog.actor_user_agent?.substring(0, 80) || "-"}</Text>
                <Text size="sm"><b>Zaman:</b> {new Date(openLog.timestamp).toLocaleString("tr-TR")}</Text>
                <Text size="sm"><b>Request ID:</b> <code>{openLog.request_id || "-"}</code></Text>
              </Stack>
            </Card>

            <Tabs defaultValue="diff">
              <Tabs.List>
                <Tabs.Tab value="diff">Değişim (Diff)</Tabs.Tab>
                <Tabs.Tab value="before">Önce</Tabs.Tab>
                <Tabs.Tab value="after">Sonra</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="diff" pt="md">
                {openLog.diff ? (
                  <Stack gap="xs">
                    {Object.entries(openLog.diff).map(([field, [oldV, newV]]: any) => (
                      <Card key={field} padding="xs" radius="sm" withBorder>
                        <Title order={6}>{field}</Title>
                        <Group gap="xs" mt={4}>
                          <Code color="red">{JSON.stringify(oldV)}</Code>
                          <Text size="xs">→</Text>
                          <Code color="green">{JSON.stringify(newV)}</Code>
                        </Group>
                      </Card>
                    ))}
                  </Stack>
                ) : <Text c="dimmed">Diff bilgisi yok</Text>}
              </Tabs.Panel>

              <Tabs.Panel value="before" pt="md">
                <Code block>{JSON.stringify(openLog.before, null, 2)}</Code>
              </Tabs.Panel>
              <Tabs.Panel value="after" pt="md">
                <Code block>{JSON.stringify(openLog.after, null, 2)}</Code>
              </Tabs.Panel>
            </Tabs>
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}
